import React, { useMemo, useState } from "react";
import {
  X, Upload, Loader2, Sparkles, RefreshCw, ArrowRight, AlertCircle,
  CheckCircle2, CircleAlert, Circle, FileImage,
} from "lucide-react";
import { useAIImport } from "./hooks/useAIImport";

/**
 * ImportPosterModal — AI-driven poster import for Events V2.
 *
 *   idle       → dropzone
 *   analyzing  → progress + rotating status message
 *   success    → structured preview (found / to-verify / missing)
 *   error      → error card with Retry / Cancel
 *
 * Flow: after the user confirms the preview, the extracted data + poster URL
 * are stashed into `sessionStorage` under key `ai_import_<audit_id>` and the
 * component calls `onCompile(auditId)` — the parent then navigates to
 * `/admin/eventi-v2/nuovo?import=<auditId>` and EditorV2 hydrates from there.
 */

const FIELD_LABELS = {
  title: "Titolo",
  subtitle: "Sottotitolo",
  short_description: "Descrizione breve",
  full_description: "Descrizione completa",
  start_date: "Data inizio",
  start_time: "Ora inizio",
  end_date: "Data fine",
  end_time: "Ora fine",
  venue_name: "Luogo",
  address: "Indirizzo",
  city: "Comune",
  category: "Categoria",
  price_text: "Prezzo",
  is_free: "Ingresso libero",
  phone: "Telefono",
  email: "Email",
  website: "Sito web",
  booking_url: "Link prenotazione",
  organizer: "Organizzatore",
  booking_required: "Su prenotazione",
  limited_seats: "Posti limitati",
  notes: "Note",
};

const MAX_MB = 8;
const ACCEPTED = ".jpg,.jpeg,.png,.webp,.pdf";
const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];

const confidenceColor = (level) => {
  if (level === "high") return "bg-emerald-100 text-emerald-800 ring-emerald-200";
  if (level === "medium") return "bg-amber-100 text-amber-800 ring-amber-200";
  if (level === "low") return "bg-rose-100 text-rose-800 ring-rose-200";
  return "bg-slate-100 text-slate-600 ring-slate-200";
};

const formatValue = (k, v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Sì" : "No";
  if (typeof v === "string" && v.length > 90) return v.slice(0, 90) + "…";
  return String(v);
};

// ---------------------------------------------------------------------------
// Dropzone
// ---------------------------------------------------------------------------
const Dropzone = ({ onFile, disabled }) => {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);

  const acceptFile = (f) => {
    setError(null);
    if (!f) return;
    if (!ACCEPTED_MIME.includes(f.type) && !/\.(jpe?g|png|webp|pdf)$/i.test(f.name)) {
      setError("Formato non supportato. Ammessi: JPG, PNG, WEBP, PDF.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setError(`File troppo grande (max ${MAX_MB} MB).`);
      return;
    }
    onFile(f);
  };

  return (
    <div>
      <label
        htmlFor="poster-file-input"
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); if (!disabled) acceptFile(e.dataTransfer?.files?.[0]); }}
        className={`block cursor-pointer border-2 border-dashed rounded-2xl p-12 text-center transition ${
          disabled ? "opacity-60 cursor-not-allowed border-slate-200" :
          dragging ? "border-emerald-500 bg-emerald-50/40" :
          "border-slate-200 hover:border-emerald-400 hover:bg-slate-50/50"
        }`}
        data-testid="poster-dropzone"
      >
        <input
          id="poster-file-input"
          type="file"
          accept={ACCEPTED}
          onChange={(e) => acceptFile(e.target.files?.[0])}
          className="hidden"
          disabled={disabled}
          data-testid="poster-file-input"
        />
        <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
          <Upload className="w-6 h-6" />
        </div>
        <div className="text-slate-800 font-medium">Trascina qui la locandina o clicca per caricare</div>
        <div className="mt-1 text-xs text-slate-500">JPG · PNG · WEBP · PDF — max {MAX_MB} MB</div>
      </label>
      {error && (
        <div className="mt-3 flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Loading state
// ---------------------------------------------------------------------------
const AnalyzingState = ({ stepMessage }) => (
  <div className="py-14 text-center" data-testid="analyzing-state">
    <div className="relative mx-auto w-16 h-16">
      <div className="absolute inset-0 rounded-full bg-emerald-100 animate-ping" />
      <div className="relative w-16 h-16 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg">
        <Sparkles className="w-7 h-7" />
      </div>
    </div>
    <div className="mt-6 text-slate-800 font-medium">{stepMessage}</div>
    <div className="mt-2 text-xs text-slate-500 flex items-center justify-center gap-1.5">
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      In genere richiede 5–15 secondi
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------
const PreviewState = ({ result, onCompile, onRetry, onCancel }) => {
  const ex = result?.extracted || {};
  const conf = ex.confidence || {};
  const warnings = ex.warnings || [];
  const missing = ex.missing_fields || [];
  const [useAsCover, setUseAsCover] = useState(true);

  const foundEntries = useMemo(() => {
    const entries = [];
    for (const k of Object.keys(FIELD_LABELS)) {
      if (["short_description", "full_description"].includes(k)) continue;
      const v = ex[k];
      if (v !== null && v !== undefined && v !== "") {
        entries.push({ key: k, value: v, confidence: conf[k] || "high" });
      }
    }
    return entries;
  }, [ex, conf]);

  const highCount = foundEntries.filter((e) => e.confidence === "high").length;
  const toVerify = foundEntries.filter((e) => e.confidence !== "high");

  return (
    <div data-testid="preview-state">
      {/* Poster preview + summary */}
      <div className="flex flex-col md:flex-row gap-4 mb-5">
        {result?.poster_url && (
          <div className="w-full md:w-40 shrink-0">
            {/\.pdf$/i.test(result.poster_url) ? (
              <div className="w-full aspect-[3/4] rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
                <FileImage className="w-8 h-8" />
              </div>
            ) : (
              <img
                src={result.poster_url}
                alt="Locandina"
                className="w-full aspect-[3/4] object-cover rounded-lg border border-slate-200"
              />
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-emerald-700 font-semibold uppercase tracking-widest">
            Analisi completata
          </div>
          <h3 className="mt-1 text-xl font-semibold text-slate-900 leading-tight break-words">
            {ex.title || <span className="text-slate-400">Titolo non riconosciuto</span>}
          </h3>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 px-2.5 py-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> {foundEntries.length} campi trovati
            </span>
            {toVerify.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2.5 py-1">
                <CircleAlert className="w-3.5 h-3.5" /> {toVerify.length} da verificare
              </span>
            )}
            {missing.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 ring-1 ring-slate-200 px-2.5 py-1">
                <Circle className="w-3.5 h-3.5" /> {missing.length} campi mancanti
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white px-2.5 py-1">
              {result?.provider || "AI"}
            </span>
          </div>
        </div>
      </div>

      {/* Found fields */}
      {foundEntries.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden mb-4" data-testid="found-fields">
          <div className="px-4 py-2.5 bg-slate-50 text-xs font-semibold uppercase tracking-widest text-slate-600">
            Campi trovati ({foundEntries.length}) · {highCount} con alta affidabilità
          </div>
          <div className="divide-y divide-slate-100">
            {foundEntries.map(({ key, value, confidence }) => (
              <div key={key} className="px-4 py-2 flex items-start gap-3 text-sm">
                <div className="w-36 shrink-0 text-slate-500 pt-0.5">{FIELD_LABELS[key] || key}</div>
                <div className="flex-1 min-w-0 text-slate-800 break-words">{formatValue(key, value)}</div>
                <span
                  className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${confidenceColor(confidence)}`}
                  title={`Confidenza ${confidence}`}
                >
                  {confidence}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missing fields */}
      {missing.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 mb-4" data-testid="missing-fields">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2">
            Campi non trovati — da completare a mano
          </div>
          <div className="flex flex-wrap gap-1.5">
            {missing.map((f) => (
              <span
                key={f}
                className="inline-flex items-center rounded-full bg-white ring-1 ring-slate-200 text-slate-700 px-2.5 py-1 text-xs"
              >
                {FIELD_LABELS[f] || f}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 mb-4" data-testid="ai-warnings">
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-800 mb-2 inline-flex items-center gap-1.5">
            <CircleAlert className="w-3.5 h-3.5" /> Note dell&apos;analisi
          </div>
          <ul className="text-xs text-amber-900 space-y-1 list-disc pl-4">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {/* Description preview */}
      {(ex.short_description || ex.full_description) && (
        <div className="rounded-xl border border-slate-200 overflow-hidden mb-4" data-testid="desc-preview">
          <div className="px-4 py-2.5 bg-slate-50 text-xs font-semibold uppercase tracking-widest text-slate-600">
            Anteprima descrizioni generate
          </div>
          <div className="p-4 space-y-3">
            {ex.short_description && (
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-widest mb-1">Descrizione breve</div>
                <div className="text-sm text-slate-800 leading-relaxed">{ex.short_description}</div>
              </div>
            )}
            {ex.full_description && (
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-widest mb-1 mt-3">Descrizione completa</div>
                <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">
                  {ex.full_description.length > 500
                    ? ex.full_description.slice(0, 500) + "…"
                    : ex.full_description}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cover choice — spec Sprint 2.2 */}
      <div
        className="rounded-xl border border-emerald-200 bg-emerald-50/40 px-4 py-3 mb-4"
        data-testid="cover-choice"
      >
        <div className="text-xs text-emerald-900 leading-snug mb-2">
          ✅ La locandina verrà conservata come documento ufficiale dell&apos;evento.
        </div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={useAsCover}
            onChange={(e) => setUseAsCover(e.target.checked)}
            className="mt-0.5 accent-emerald-600"
            data-testid="cover-choice-checkbox"
          />
          <div className="text-xs text-slate-800">
            <div className="font-medium">Usa questa locandina anche come immagine di copertina</div>
            <div className="text-slate-600 mt-0.5">
              Potrai comunque cambiarla in seguito dall&apos;editor con una foto o una grafica diversa.
            </div>
          </div>
        </label>
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm text-slate-600 hover:bg-slate-50"
          data-testid="preview-cancel"
        >
          Annulla
        </button>
        <button
          type="button"
          onClick={onRetry}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm border border-slate-200 text-slate-700 hover:bg-slate-50"
          data-testid="preview-retry"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Riprova
        </button>
        <button
          type="button"
          onClick={() => onCompile(useAsCover)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-700"
          data-testid="preview-compile"
        >
          Compila la scheda <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------
const ErrorState = ({ error, onRetry, onCancel }) => (
  <div className="py-8 text-center" data-testid="error-state">
    <div className="mx-auto w-14 h-14 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center mb-4">
      <AlertCircle className="w-7 h-7" />
    </div>
    <div className="text-slate-800 font-medium">Analisi non riuscita</div>
    <div className="mt-2 text-sm text-slate-500 max-w-md mx-auto">{error}</div>
    <div className="mt-6 flex items-center justify-center gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="px-4 py-2 rounded-full text-sm text-slate-600 hover:bg-slate-50"
      >
        Annulla
      </button>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800"
        data-testid="error-retry"
      >
        <RefreshCw className="w-4 h-4" /> Riprova
      </button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Modal shell
// ---------------------------------------------------------------------------
export default function ImportPosterModal({ open, onClose, onCompile }) {
  const { status, result, error, stepMessage, run, cancel } = useAIImport();
  const [file, setFile] = useState(null);

  if (!open) return null;

  const handleFile = async (f) => {
    setFile(f);
    try { await run(f); } catch { /* handled in state */ }
  };

  const handleRetry = async () => {
    if (file) {
      try { await run(file); } catch { /* handled */ }
    }
  };

  const handleClose = () => {
    cancel();
    setFile(null);
    onClose?.();
  };

  const handleCompile = (useAsCover = true) => {
    if (!result) return;
    const auditId = result.audit_id || `import-${Date.now()}`;
    try {
      window.sessionStorage.setItem(
        `ai_import_${auditId}`,
        JSON.stringify({ ...result, use_poster_as_cover: !!useAsCover }),
      );
    } catch { /* quota — best-effort */ }
    onCompile?.(auditId);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/40 backdrop-blur-sm p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-poster-title"
      data-testid="import-poster-modal"
    >
      <div className="w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 sm:px-6 py-4 border-b border-slate-100">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> AI Beta
            </div>
            <h2 id="import-poster-title" className="mt-0.5 text-lg font-semibold text-slate-900">
              Importa da locandina
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
            aria-label="Chiudi"
            data-testid="import-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1">
          {status === "idle" && (
            <>
              <p className="text-sm text-slate-500 mb-5">
                Carica la locandina dell&apos;evento. L&apos;AI estrarrà automaticamente titolo, date,
                luogo, orari, contatti e descrizione. Nulla verrà pubblicato — creerai comunque una bozza da rivedere.
              </p>
              <Dropzone onFile={handleFile} disabled={false} />
            </>
          )}
          {status === "analyzing" && <AnalyzingState stepMessage={stepMessage} />}
          {status === "success" && result && (
            <PreviewState
              result={result}
              onCompile={handleCompile}
              onRetry={handleRetry}
              onCancel={handleClose}
            />
          )}
          {status === "error" && (
            <ErrorState error={error} onRetry={handleRetry} onCancel={handleClose} />
          )}
        </div>
      </div>
    </div>
  );
}
